import { useSelector } from 'react-redux';

import { DeviceType, DeviceTypeLabels, DeviceTypes } from 'src/constants/DeviceTypes';
import { RoomTypeLabels, RoomTypes } from 'src/constants/RoomTypes';
import { selectIsDoorAlertCareConfirmationEnabled } from 'src/store/configSlice';
import type { AllocationType, Device, DeviceAllocation } from 'src/store/sageAdminApi';
import { getDevicesFromAllocation } from 'src/utils/getDevicesFromAllocation';

/**
 * Who starts the confirmation flow - the device, or the user in-app
 */
export type CareConfirmationMode = 'none' | 'app-initiated' | 'device-initiated';

export interface AllocationTypeConfig {
    /** Singular display name, e.g. "Pendant", "Door Sensor" */
    displayName: string;
    /** Default icon. 'check-in' is applied at runtime when a button's allocation.isCheckIn is true. */
    iconType: DeviceIconType;
    /** Who starts the confirmation flow. A facility flag can upgrade doors to 'app-initiated'. */
    careConfirmationMode: CareConfirmationMode;
    /** What to do to the hardware. Only set for 'device-initiated' types, which have no app flow. */
    onDeviceConfirmationInstruction?: string;
    /** True for BUTTON allocations (pendants). */
    isButton: boolean;
    /** True for UNIVERSAL_TRANSMITTER / UNIVERSAL_TRANSMITTER_BUTTON allocations. */
    isAccessibilityDevice: boolean;
    /** True for DOOR / OFFLINE_DOOR / ONLINE_DOOR allocations. */
    isDoorAllocationType: boolean;
    /** True for FALL_DETECTION_DEVICE allocations. */
    isFallDetectionDevice: boolean;
}

/**
 * Single source of truth for per-allocation-type display and alert behavior. Adding an
 * AllocationType requires an entry here and a DeviceVariant below; TypeScript will tell you.
 */
export const DEFAULT_ALLOCATION_TYPE_CONFIG: Record<AllocationType, AllocationTypeConfig> = {
    BUTTON: {
        displayName: 'Pendant',
        iconType: 'pendant',
        careConfirmationMode: 'app-initiated',
        isButton: true,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    BUTTON_WITH_RESET: {
        displayName: 'Pendant',
        iconType: 'button-with-reset',
        // An Inovonics alert clears when the device itself is reset, not from the app.
        careConfirmationMode: 'device-initiated',
        isButton: true,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    CARE_CONFIRMATION_BUTTON: {
        displayName: 'Care Confirmation Button',
        iconType: 'pendant',
        careConfirmationMode: 'none',
        isButton: true,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    CHECKIN_MOVEMENT_SENSOR: {
        displayName: 'Check-in Sensor',
        iconType: 'check-in-sensor',
        careConfirmationMode: 'none',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    DOOR: {
        displayName: 'Door Sensor',
        iconType: 'door-alarm',
        careConfirmationMode: 'none',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: true,
        isFallDetectionDevice: false,
    },
    FALL_DETECTION_DEVICE: {
        displayName: 'Fall Detection Device',
        iconType: 'fall-detection',
        careConfirmationMode: 'app-initiated',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: true,
    },
    M3: {
        displayName: 'M3 Device',
        iconType: 'unassigned',
        // A Melten alert is confirmed by interacting with the device, with no app flow.
        careConfirmationMode: 'device-initiated',
        onDeviceConfirmationInstruction: 'Press the Cancel button on the device to confirm it',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    OFFLINE_DOOR: {
        displayName: 'Offline Door',
        iconType: 'door-alarm',
        careConfirmationMode: 'none',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: true,
        isFallDetectionDevice: false,
    },
    ONLINE_DOOR: {
        displayName: 'Online Door',
        iconType: 'door-alarm',
        careConfirmationMode: 'none',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: true,
        isFallDetectionDevice: false,
    },
    PULL_CORD: {
        displayName: 'Pull Cord',
        iconType: 'pull-cord',
        careConfirmationMode: 'device-initiated',
        isButton: false,
        isAccessibilityDevice: false,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    UNIVERSAL_TRANSMITTER: {
        displayName: 'Accessibility Device',
        iconType: 'accessibility-device',
        careConfirmationMode: 'app-initiated',
        isButton: false,
        isAccessibilityDevice: true,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
    UNIVERSAL_TRANSMITTER_BUTTON: {
        displayName: 'Accessibility Device',
        iconType: 'accessibility-device',
        careConfirmationMode: 'app-initiated',
        isButton: false,
        isAccessibilityDevice: true,
        isDoorAllocationType: false,
        isFallDetectionDevice: false,
    },
};

/**
 * A device's user-facing flavor, collapsing allocationType and what it is assigned to. A BUTTON is
 * a pendant or a unit button depending on assignment, so the type alone cannot drive display.
 */
export type DeviceVariant =
    | 'resident-pendant'
    | 'unit-button'
    | 'button'
    | 'pull-cord'
    | 'check-in-button'
    | 'check-in-sensor'
    | 'door-sensor'
    | 'universal-transmitter'
    | 'accessibility-device'
    | 'fall-detection'
    | 'm3'
    | 'unknown';

interface DeviceVariantContext {
    hasResident: boolean;
    hasUnit: boolean;
    isCheckIn: boolean;
    deviceType?: DeviceType;
}

/**
 * A button cannot be named from its allocationType alone. An unassigned one is not a pendant or a
 * unit button yet, so it is just a button. Check-in only applies to unit buttons, so a pendant wins.
 */
const getButtonVariant = ({
    hasResident,
    hasUnit,
    isCheckIn,
}: DeviceVariantContext): DeviceVariant => {
    if (hasResident && !hasUnit) return 'resident-pendant';
    if (isCheckIn) return 'check-in-button';
    return hasUnit ? 'unit-button' : 'button';
};

/** Door allocations hold both the sensor and, for online/offline doors, a transmitter. */
const getDoorVariant = ({ deviceType }: DeviceVariantContext): DeviceVariant =>
    deviceType === DeviceTypes.UniversalTransmitter ? 'universal-transmitter' : 'door-sensor';

/** Single source of truth for allocationType -> variant. */
const VariantByAllocationType: Record<
    AllocationType,
    (context: DeviceVariantContext) => DeviceVariant
> = {
    BUTTON: getButtonVariant,
    BUTTON_WITH_RESET: getButtonVariant,
    CARE_CONFIRMATION_BUTTON: getButtonVariant,
    CHECKIN_MOVEMENT_SENSOR: () => 'check-in-sensor',
    DOOR: getDoorVariant,
    FALL_DETECTION_DEVICE: () => 'fall-detection',
    M3: () => 'm3',
    OFFLINE_DOOR: getDoorVariant,
    ONLINE_DOOR: getDoorVariant,
    PULL_CORD: () => 'pull-cord',
    UNIVERSAL_TRANSMITTER: () => 'accessibility-device',
    UNIVERSAL_TRANSMITTER_BUTTON: () => 'accessibility-device',
};

/**
 * Fallback for when we know the hardware but not the allocation, which lags behind on alert
 * screens. A plain button stays 'unknown' — without an allocation it could be either flavor.
 */
const VariantByDeviceType: Record<DeviceType, DeviceVariant> = {
    [DeviceTypes.Button]: 'unknown',
    [DeviceTypes.DoorAlarm]: 'door-sensor',
    [DeviceTypes.FallDetectionDevice]: 'fall-detection',
    [DeviceTypes.M3]: 'm3',
    [DeviceTypes.UniversalTransmitter]: 'universal-transmitter',
    [DeviceTypes.MovementSensor]: 'check-in-sensor',
};

export function getDeviceVariant(allocation?: DeviceAllocation, device?: Device): DeviceVariant {
    const { allocationType, resident, unit, isCheckIn } = allocation || {};
    const deviceType = device?.type as DeviceType | undefined;

    if (!allocationType) {
        return (deviceType && VariantByDeviceType[deviceType]) ?? 'unknown';
    }

    return (
        VariantByAllocationType[allocationType]?.({
            hasResident: !!resident,
            hasUnit: !!unit,
            isCheckIn: !!isCheckIn,
            deviceType,
        }) ?? 'unknown'
    );
}

export type DeviceIconType =
    | 'pendant'
    | 'button-with-reset'
    | 'pull-cord'
    | 'check-in'
    | 'check-in-sensor'
    | 'accessibility-device'
    | 'door-alarm'
    | 'fall-detection'
    | 'unassigned';

/** Check-in sensors are flagged isCheckIn too, so the flag alone does not make a check-in button. */
const isCheckInButton = (allocation?: DeviceAllocation): boolean =>
    allocation?.allocationType === 'BUTTON' && !!allocation.isCheckIn;

export function getDeviceIconType(allocation: DeviceAllocation | undefined): DeviceIconType {
    if (!allocation?.unit && !allocation?.resident && !allocation?.door) return 'unassigned';
    if (isCheckInButton(allocation)) return 'check-in';
    return allocation.allocationType
        ? DEFAULT_ALLOCATION_TYPE_CONFIG[allocation.allocationType]?.iconType ?? 'unassigned'
        : 'unassigned';
}

export const CheckInButtonLabel = 'Check-in Button';

export const getRoomDisplayValue = (deviceAllocation: DeviceAllocation): string => {
    if (isCheckInButton(deviceAllocation)) {
        return CheckInButtonLabel;
    } else if (
        !deviceAllocation ||
        !deviceAllocation.room ||
        deviceAllocation.room === RoomTypes.None ||
        deviceAllocation.resident ||
        deviceAllocation.door
    ) {
        return '';
    } else {
        return RoomTypeLabels[deviceAllocation.room];
    }
};

export function getDeviceDisplayName(
    allocation: DeviceAllocation | undefined,
    device?: Device
): string {
    if (isCheckInButton(allocation)) return CheckInButtonLabel;

    const isUnassigned =
        !!allocation && !allocation.resident && !allocation.unit && !allocation.door;

    if (isUnassigned) {
        return device?.hardwareId || DeviceTypeLabels[device?.type as DeviceType] || 'Device';
    }

    if (
        allocation?.allocationType === 'BUTTON' ||
        allocation?.allocationType === 'BUTTON_WITH_RESET'
    ) {
        return allocation.resident ? 'Resident Pendant' : 'Unit Button';
    }

    const deviceTypeLabel = DeviceTypeLabels[device?.type as DeviceType];
    const allocationConfig = allocation?.allocationType
        ? DEFAULT_ALLOCATION_TYPE_CONFIG[allocation.allocationType]
        : undefined;

    if (allocationConfig?.isDoorAllocationType) {
        const isOnlineDoorTransmitter =
            allocation?.allocationType === 'ONLINE_DOOR' &&
            device?.type === DeviceTypes.UniversalTransmitter;

        return isOnlineDoorTransmitter || !deviceTypeLabel
            ? allocationConfig.displayName
            : deviceTypeLabel;
    }

    return allocationConfig?.displayName || deviceTypeLabel || 'Device';
}

/** How each variant is named. Empty means callers fall back to the hardware ID or type. */
export const DeviceVariantLabels: Record<DeviceVariant, string> = {
    'resident-pendant': 'Resident Pendant',
    'unit-button': 'Unit Button',
    button: 'Button',
    'pull-cord': 'Pull Cord',
    'check-in-button': CheckInButtonLabel,
    'check-in-sensor': 'Check-in Sensor',
    'door-sensor': 'Door Sensor',
    'universal-transmitter': 'Universal Transmitter',
    'accessibility-device': 'Accessibility Device',
    'fall-detection': 'Fall Detection Device',
    m3: 'Melten M3 Device',
    unknown: '',
};

export interface DeviceDisplayInfo {
    title: string;
    subtitle: string;
}

/** Most devices are located by their room; the exceptions are named here. */
function getDeviceSubtitle(
    variant: DeviceVariant,
    allocation: DeviceAllocation | undefined,
    hardwareId: string
): string {
    if (variant === 'resident-pendant' || variant === 'button') return hardwareId;
    if (variant === 'check-in-button') return '';

    return allocation ? getRoomDisplayValue(allocation) : '';
}

/** Title and subtitle for the device that created an alert or care event. */
export function getDeviceVariantDisplayInfo(
    allocation?: DeviceAllocation,
    device?: Device
): DeviceDisplayInfo {
    const variant = getDeviceVariant(allocation, device);
    const hardwareId = device?.hardwareId || '';

    return {
        // Nothing to name it by: no variant label, and no hardware to fall back on.
        title: DeviceVariantLabels[variant] || hardwareId || 'Unknown',
        subtitle: getDeviceSubtitle(variant, allocation, hardwareId),
    };
}

export interface DeviceAllocationOverrides {
    /** Facility config: when on, door alerts require care confirmation regardless of type. */
    isDoorAlertConfirmationRequired?: boolean;
}

/**
 * The mode after facility overrides. An unknown type is assumed app-initiated, since that flow is
 * how a caregiver proves they attended and guessing the other way silently skips it.
 */
export function getCareConfirmationMode(
    deviceAllocation?: DeviceAllocation,
    { isDoorAlertConfirmationRequired = false }: DeviceAllocationOverrides = {}
): CareConfirmationMode {
    const { allocationType } = deviceAllocation || {};
    const config = allocationType ? DEFAULT_ALLOCATION_TYPE_CONFIG[allocationType] : undefined;

    // A facility can opt all of its doors into the normal caregiver confirmation flow.
    if (config?.isDoorAllocationType && isDoorAlertConfirmationRequired) return 'app-initiated';

    return config?.careConfirmationMode ?? 'app-initiated';
}

/** Everything a screen needs about one allocation. Pure; the hook supplies the facility override. */
export function getDeviceAllocationProperties(
    deviceAllocation?: DeviceAllocation,
    { isDoorAlertConfirmationRequired = false }: DeviceAllocationOverrides = {}
) {
    const { isCheckIn, door, resident, unit, room, allocationType } = deviceAllocation || {};

    const { devices } = getDevicesFromAllocation(deviceAllocation);

    const {
        isButton = false,
        isAccessibilityDevice = false,
        isDoorAllocationType = false,
        isFallDetectionDevice = false,
        onDeviceConfirmationInstruction,
    } = (allocationType ? DEFAULT_ALLOCATION_TYPE_CONFIG[allocationType] : undefined) ?? {};

    const careConfirmationMode = getCareConfirmationMode(deviceAllocation, {
        isDoorAlertConfirmationRequired,
    });

    // "Allocated" means that the device is allocated to a facility.
    // "Assigned" means that the device is assigned to a resident, unit, or door.
    const isAssigned = !!resident || !!unit || !!door;

    const isDoorDevice =
        !!devices?.some(
            (device) =>
                device?.type === DeviceTypes.DoorAlarm ||
                device?.type === DeviceTypes.UniversalTransmitter
        ) && isDoorAllocationType;

    // Not from getDeviceVariant: a check-in button is still a unit button here.
    const isUnitButton = isButton && !!unit && !resident;
    const isResidentPendant = isButton && !unit && !!resident;
    const isCheckInSensor = allocationType === 'CHECKIN_MOVEMENT_SENSOR';

    return {
        careConfirmationMode,
        devices,
        door,
        onDeviceConfirmationInstruction,
        isAccessibilityDevice,
        isAssigned,
        isButton,
        isCheckIn,
        isCheckInSensor,
        isDoorDevice,
        isFallDetectionDevice,
        isResidentPendant,
        isUnitButton,
        resident,
        room,
        unit,
    };
}

/** Reads the facility config override and hands off to the pure resolver above. */
function useDeviceAllocationProperties(deviceAllocation?: DeviceAllocation) {
    const isDoorAlertConfirmationRequired = useSelector(selectIsDoorAlertCareConfirmationEnabled);

    return getDeviceAllocationProperties(deviceAllocation, { isDoorAlertConfirmationRequired });
}

export default useDeviceAllocationProperties;
